/**
 * [spec-019 T005, US-5] O OBSERVADOR da sessão de voz.
 *
 * ★ WHY um observador de ESTADO, e não mais um caminho lembrando de fechar o
 * seu: `research.md` da spec-019 mediu QUATRO caminhos diferentes (D1-D4)
 * que levam ao MESMO estado terminal — sessão ativa, mic fechado, ninguém
 * falando, nenhum aviso —, e a spec-017 T003 já tinha corrigido dois desses
 * caminhos sem alcançar o terceiro (o principal, `onEnviarVoz`). Enquanto a
 * recuperação depender de CADA caminho lembrar de fechar a própria fala, o
 * PRÓXIMO caminho novo nasce quebrado do mesmo jeito. Um observador que só
 * olha "isto ainda está silenciosamente travado?" não depende de nenhum
 * caminho específico ter sido escrito certo — cobre os quatro conhecidos e
 * qualquer quinto que ainda não existe.
 *
 * ADR-019-01: mora num módulo À PARTE, sem tocar `motorDeVoz`/`iniciar()`
 * diretamente — recebe tudo por injeção (`estaEmSilencioTerminal`,
 * `temAvisos`, `recuperar`, `avisar`, `encerrar`). Quem monta a factory com
 * os callbacks reais é `motor-de-voz.js`, o único módulo que detém
 * `_avisosAtuais` e `iniciar()`. [G-4 SHOULD, review spec-019, 10/09]
 * Extraído de `motor-de-voz.js` (que passou de 514 para 644 linhas ao
 * ganhar este observador, acima do teto de 500 do CLAUDE.md raiz) — mesmo
 * motivo das três extrações anteriores documentadas no cabeçalho de
 * `motor-de-voz.js` (estado-da-voz.js, vad-de-voz.js, fala-da-ia.js).
 * ADR-019-02: RECUPERA e AVISA — nunca só recupera. Recuperar em silêncio
 * esconderia a PRÓXIMA regressão: o sintoma sumiria, a causa ficaria, e
 * ninguém voltaria a olhar (mesmo racional do `_recuperarOuEncerrarSemDesfecho`
 * do `apiChat.js`, que pinta a bolha em vez de engolir o turno morto).
 * ADR-019-05 [G-3 SHOULD, review spec-019, 10/09]: a recuperação tem TETO.
 * Sem ele, um microfone que não pode mais ser adquirido (permissão
 * revogada, device removido) reentra no estado terminal 30s depois da
 * "recuperação" e dispara outra — indefinidamente, empilhando avisos sobre
 * o texto que o consultor está tentando ler (o oposto do que o próprio
 * ADR-019-04 de `fala-da-ia.js` existe para evitar). Ao esgotar as
 * tentativas consecutivas, desiste pela MESMA régua do D5 abaixo — não é um
 * terceiro tipo de desfecho, é a mesma "não posso reabrir sozinho, avise e
 * pare" com origem diferente.
 */

// [AC4] Limiar NOMEADO. Precisa ser MAIOR que a maior espera LEGÍTIMA
// conhecida — o relógio de progresso da spec-012 fala aos 8s e repete a
// cada 20s (turno "em voo"); a síntese de UMA frase tem prazo de 12s
// (T002, `PRAZO_MS_SINTESE_DE_FRASE`). Por construção, `estaEmSilencioTerminal`
// (montado por `motor-de-voz.js`) já não deveria disparar falso-positivo
// durante nenhuma dessas esperas (a IA falando OU o turno em voo cobrem os
// três casos por `algoEmVooNaFala`/`estadoDaFala`) — mas 30s dá margem de
// segurança contra qualquer blind spot que a medição não tenha coberto, sem
// fazer o consultor esperar perto de um minuto por uma recuperação
// automática.
export const LIMIAR_MS_OBSERVADOR_DE_SILENCIO = 30000

// Granularidade do polling — não dá pra `$watch` `algoEmVooNaFala()` (não é
// reativo, é um getter de closures de `fala-da-ia.js`), então o observador
// checa por INTERVALO. `TICKS_LIMIAR_OBSERVADOR` ticks consecutivos no
// estado terminal = o limiar acima, sem precisar de `Date.now()` (Jest 24
// fake timers legados não mockam `Date` — mesma cautela de `vad-de-voz.js`).
const INTERVALO_MS_VERIFICACAO_SILENCIO = 1000
const TICKS_LIMIAR_OBSERVADOR = Math.ceil(
  LIMIAR_MS_OBSERVADOR_DE_SILENCIO / INTERVALO_MS_VERIFICACAO_SILENCIO
)

// [G-3 SHOULD] Quantas vezes seguidas o observador tenta `recuperar()` antes
// de desistir. `1` = a PRIMEIRA vez que o silêncio terminal se sustenta,
// tenta recuperar; se 30s depois o estado terminal se repete (a tentativa
// não funcionou — mic não voltou a gravar, IA não voltou a falar), desiste
// na hora em vez de tentar pra sempre.
export const MAX_TENTATIVAS_RECUPERACAO_CONSECUTIVAS = 1

// [ADR-019-02] Aviso PRÓPRIO do observador — nunca reusa a mensagem de
// falha de síntese/transcrição, porque o FATO é outro: aqui ninguém
// reportou erro nenhum, o observador só percebeu silêncio sustentado.
export const MENSAGEM_OBSERVADOR_RECUPEROU =
  'Fiquei em silêncio por um tempo e reabri o microfone sozinho — se isso se repetir, avise o suporte.'

// [D5 + G-3] A reabertura pode ser um no-op SILENCIOSO quando não há mais
// como tentar de novo — seja porque `temAvisos()` é falso (D5 do
// `research.md` da spec-019: `_avisosAtuais` nulo), seja porque as
// tentativas consecutivas já se esgotaram (G-3: a recuperação foi tentada e
// o estado terminal voltou). Nos dois casos, chamar `recuperar()` de novo
// não resolveria nada e esconderia o problema atrás de uma sessão que
// parece viva mas nunca mais ouve ninguém. Detecta e ENCERRA com aviso
// explícito, em vez de tentar reabrir e mentir pra si mesmo.
export const MENSAGEM_OBSERVADOR_SEM_AVISOS =
  'O Modo de Voz ficou em silêncio e não consegui reabrir o microfone sozinho — saia e entre de novo.'

/**
 * Factory pura (sem tocar `motorDeVoz`/DOM) — mesmo molde de
 * `barge-in-de-voz.js::criarEscutaDeBargeIn`: quem chama decide QUANDO
 * observar (`iniciar`/`parar`), a maquinaria de polling mora aqui.
 *
 * @param {Object} opts
 * @param {() => boolean} opts.estaEmSilencioTerminal As TRÊS condições
 *   legítimas (mic armado, IA falando, turno em voo) já checadas por quem
 *   monta — este módulo só observa o resultado.
 * @param {() => boolean} opts.temAvisos Se existe um `avisos` guardado que
 *   permite tentar `recuperar()` de novo (D5).
 * @param {Function} opts.recuperar Reabre o mic — chamado com a sessão
 *   ainda ativa e `temAvisos()` verdadeiro e tentativas disponíveis.
 * @param {(mensagem: string) => void} opts.avisar Mostra o aviso ao
 *   consultor (mesmo canal de falha de transcrição/síntese).
 * @param {Function} opts.encerrar Encerra a sessão de voz (D5/G-3: quando
 *   não há mais o que tentar).
 * @returns {{iniciar: () => void, parar: () => void}}
 */
export const criarObservadorDeSilencio = ({
  estaEmSilencioTerminal,
  temAvisos,
  recuperar,
  avisar,
  encerrar,
}) => {
  let _intervalo = null
  let _ticksConsecutivosEmSilencio = 0
  let _tentativasDeRecuperacaoConsecutivas = 0

  const _recuperarOuDesistir = () => {
    const podeTentarDeNovo =
      temAvisos() &&
      _tentativasDeRecuperacaoConsecutivas < MAX_TENTATIVAS_RECUPERACAO_CONSECUTIVAS
    if (podeTentarDeNovo) {
      _tentativasDeRecuperacaoConsecutivas += 1
      avisar(MENSAGEM_OBSERVADOR_RECUPEROU)
      recuperar()
      return
    }
    avisar(MENSAGEM_OBSERVADOR_SEM_AVISOS)
    encerrar()
  }

  const _verificar = () => {
    if (!estaEmSilencioTerminal()) {
      _ticksConsecutivosEmSilencio = 0
      // [G-3] Saiu do estado terminal por conta própria (mic voltou a
      // gravar, IA voltou a falar) — a próxima entrada em silêncio começa
      // um contador de tentativas NOVO, não herda o desgaste da anterior.
      _tentativasDeRecuperacaoConsecutivas = 0
      return
    }
    _ticksConsecutivosEmSilencio += 1
    if (_ticksConsecutivosEmSilencio >= TICKS_LIMIAR_OBSERVADOR) {
      _ticksConsecutivosEmSilencio = 0
      _recuperarOuDesistir()
    }
  }

  // [AC4 / G-1 SHOULD, review spec-019, 10/09] `parar()` é a ÚNICA saída do
  // temporizador — testado diretamente contra `jest.getTimerCount()` em
  // `observador-de-silencio.unit.js`, não só pelo EFEITO (não avisou, não
  // reabriu) como o teste antigo fazia. Um `setInterval` que sobrevive sem
  // ser observado por nenhum teste é exatamente o "temporizador órfão" que
  // o AC4 proíbe.
  const parar = () => {
    if (_intervalo) {
      clearInterval(_intervalo)
      _intervalo = null
    }
    _ticksConsecutivosEmSilencio = 0
    _tentativasDeRecuperacaoConsecutivas = 0
  }

  const iniciar = () => {
    if (_intervalo) return
    _ticksConsecutivosEmSilencio = 0
    _tentativasDeRecuperacaoConsecutivas = 0
    _intervalo = setInterval(_verificar, INTERVALO_MS_VERIFICACAO_SILENCIO)
  }

  return { iniciar, parar }
}
